//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext } from 'react';
import { withStyles } from '@material-ui/core/styles';
import FlagIcon from '@material-ui/icons/Flag';
import TextTruncate from 'react-text-truncate';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getUserDateFormat } from '../../common/dateHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdBadgeCount from '../../common/elemental/AfitdBadgeCount';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import LastMessageAgeCounter from '../../dashboardMyMessages/subComponents/LastMessageAgeCounter';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const styles = theme => ({
    root: {
        display: 'flex',
        padding: '14px 24px 14px 18px',
        borderLeft: '4px solid transparent',
        '&:hover': {
            borderLeft: '4px solid #1D8B8C',
            background: '#FCFCFC',
            cursor: 'pointer',
        },
    },
    currentThread: {
        borderLeft: '4px solid #1D8B8C',
        background: '#FCFCFC',
        '&:hover': {
            cursor: 'auto',
        },
    },

    profileImage: {
        // clipPath: 'circle(50% at center)',
        // '-webkit-clip-path': 'circle(50% at center)',
        width: 40,
        height: 40,
    },
    messageLeft: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginRight: '16px',
        width: '42px',
    },
    messageLeftLink: {
        display: 'block',
        clipPath: 'circle(60px at center)',
    },
    flagIconContainer: {
        marginTop: '5px',
    },
    messageRight: {
        flexGrow: 1,
    },

    messageRightLine1: {
        display: 'flex',
        justifyContent: 'space-between',
    },

    name: {
        position: 'relative',
        fontSize: '14px',
        color: '#9c9e9d',
        padding: '0 0 6px 0',
        fontWeight: 'normal',
        lineHeight: '16px',
    },

    lastMessageDt: {
        fontSize: '14px',
        color: '#9c9e9d',
    },

    messageRightLine2: {
        display: 'flex',
        justifyContent: 'space-between',
    },

    subject: {
        position: 'relative',
        fontSize: '16px',
        color: '#131f15',
        padding: '0 50px 6px 0',
        fontWeight: '500',
        lineHeight: '22px',
    },

    unreadMessageCount: {},

    messageRightLine3: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
    },

    lastMessage: {
        color: '#111E14',
        fontSize: '14px',
        fontWeight: 'normal',
        lineHeight: '18px',
        display: 'block',
        position: 'relative',
        width: '100%',
    },
    draftReply: {
        color: 'red',
        fontStyle: 'italic',
    },

    hidden: {
        display: 'none',
    },
});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { props, userDateFormat, userId, currentThreadId, draftReplies } = params;
    const { classes, data, onClick } = props;
    const {
        root: rootCls,
        currentThread: currentThreadCls,
        flagIconContainer: flagIconContainerCls,
        lastMessage: lastMessageCls,
        draftReply: draftReplyCls,
        hidden: hiddenCls,
    } = classes;

    const threadId = _get(data, 'threadId');
    const subject = _get(data, 'subject');
    const companyName = _get(data, 'companyName');
    const starterName = _get(data, 'starterName');
    const starterProfilePicUrl = _get(data, 'starterProfilePicUrl');
    const employerNewMessagesCount = _get(data, 'unreadCount', 0);

    const lastMessageDt = _get(data, 'lastMessageDt');
    const lastMessage = _get(data, 'lastMessage');
    const markedForFollowUp = _get(data, `followUpDt.${userId}`);

    const isCurrentThread = currentThreadId === threadId;

    let draftReply = null;
    if (!isCurrentThread) {
        draftReply = _get(draftReplies, `current.${threadId}`, '');
    }

    const defns = {
        root: {
            className: cx(rootCls, { [currentThreadCls]: isCurrentThread }),
            onClick: () => {
                onClick(data);
            },
        },
        profileImage: {
            src: starterProfilePicUrl,
            alt: `${companyName} logo`,
        },
        nameText: {
            variant: 'body2',
            clsMidGrayColor: true,
            value: starterName,
        },
        lastMessageDtText: {
            typographyProps: {
                variant: 'body2',
                clsMidGrayColor: true,
            },
            dt: lastMessageDt,
            dateFormat: userDateFormat,
        },
        subjectText: {
            variant: 'body1',
            clsLightBold: true,
            value: subject,
        },
        unreadMessageCount: {
            className: cx({ [hiddenCls]: isCurrentThread }),
        },
        unreadMessageCountText: {
            count: employerNewMessagesCount,
        },
        lastMessage: {
            className: cx(lastMessageCls, { [draftReplyCls]: !isCurrentThread && draftReply }),
        },
        lastMessageText: {
            variant: 'body2',
            value: isCurrentThread || !draftReply ? lastMessage : `Draft: ${draftReply}`,
        },
        flagIconContainer: {
            className: cx(flagIconContainerCls, { [hiddenCls]: !markedForFollowUp }),
        },
        flagIcon: {
            color: 'primary',
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ThreadSummaryBlock = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const userId = _get(appContext, 'userId');
    const employerId = _get(appContext, 'employerId');
    const draftReplies = _get(appContext, 'draftReplies');
    const currentThreadId = _get(context, 'currentThreadId');
    const userDateFormat = getUserDateFormat(appContext);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [isDirty, setIsDirty] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ props, userDateFormat, employerId, currentThreadId, draftReplies,userId });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.messageLeft}>
                <div {...defns.messageLeftLink}>
                    <AfitdAvatar {...defns.profileImage} />
                </div>
                <div {...defns.flagIconContainer}>
                    <FlagIcon fontSize="small" {...defns.flagIcon} />
                </div>
            </div>
            <section {...defns.messageRight}>
                <div {...defns.messageRightLine1}>
                    <div {...defns.name}>
                        <AfitdTypography {...defns.nameText} />
                    </div>
                    <div {...defns.lastMessageDt}>
                        <LastMessageAgeCounter {...defns.lastMessageDtText} />
                    </div>
                </div>
                <div {...defns.messageRightLine2}>
                    <div {...defns.jobTitle}>
                        <AfitdTypography {...defns.subjectText} />
                    </div>
                    <div {...defns.unreadMessageCount}>
                        <AfitdBadgeCount {...defns.unreadMessageCountText} />
                    </div>
                </div>
                <div {...defns.messageRightLine3}>
                    <div {...defns.lastMessage}>
                        <TextTruncate line={1} element="div" truncateText="…" text={defns.lastMessageText.value} />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default withStyles(styles)(ThreadSummaryBlock);
